import React, { useState, useEffect, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { Registration } from '../../types/registration';
import InvoiceDisplay from '../Financial/InvoiceDisplay';

const CamperInvoicePage: React.FC = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  const { currentUser, loading: authLoading } = useAuth();

  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (authLoading || !currentUser) return;

    const fetchRegistration = async () => {
      if (!registrationId) {
        setError('Registration ID is required');
        return;
      }

      setLoading(true);
      try {
        const registrationDoc = await getDoc(doc(db, 'Registrations', registrationId));

        if (!registrationDoc.exists()) {
          setError('Registration not found');
          setLoading(false);
          return;
        }

        const regData = { id: registrationDoc.id, ...registrationDoc.data() } as Registration;

        if (regData.status === 'Cancelled' || regData.status === 'cancelled') {
          setError('This registration has been cancelled');
          setLoading(false);
          return;
        }

        setRegistration(regData);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching registration:', err);
        setError('Failed to load invoice. Please try again.');
        setLoading(false);
      }
    };

    fetchRegistration();
  }, [registrationId, currentUser, authLoading]);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoginError('');
      setLoginLoading(true);
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found') {
        setLoginError('No account exists with this email.');
      } else if (err.code === 'auth/wrong-password') {
        setLoginError('Incorrect password.');
      } else if (err.code === 'auth/invalid-email') {
        setLoginError('Please enter a valid email address.');
      } else if (err.code === 'auth/too-many-requests') {
        setLoginError('Too many failed attempts. Please try again later.');
      } else {
        setLoginError('Failed to login. Please try again.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          padding: '2.5rem',
          maxWidth: '420px',
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img
              src="https://berwickregistration.web.app/images/berwick-logo.png"
              alt="Berwick Camp Logo"
              style={{ width: '80px', height: '80px', objectFit: 'contain', marginBottom: '1rem' }}
            />
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>View Your Invoice</h2>
            <p style={{ color: '#666', margin: 0, fontSize: '0.95rem' }}>
              Please log in to view your invoice and make a payment.
            </p>
          </div>

          {loginError && (
            <div style={{
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              padding: '0.75rem',
              color: '#c33',
              marginBottom: '1rem',
              fontSize: '0.9rem'
            }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600, color: '#333' }}>Email:</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
                disabled={loginLoading}
                placeholder="Enter your email"
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600, color: '#333' }}>Password:</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  disabled={loginLoading}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    paddingRight: '4rem'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loginLoading}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    fontSize: '0.85rem'
                  }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#1a237e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: loginLoading ? 'not-allowed' : 'pointer',
                opacity: loginLoading ? 0.7 : 1
              }}
            >
              {loginLoading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div>Loading invoice...</div>
      </div>
    );
  }

  if (error || !registration) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Error</h2>
          <p>{error || 'Invoice not found'}</p>
        </div>
      </div>
    );
  }

  const reg = registration as any;
  const paymentTotal = (reg.paymentHistory || []).reduce(
    (sum: number, payment: any) => sum + (Number(payment.amount) || 0), 0
  );

  const invoiceData = {
    id: registration.id,
    primaryCamper: {
      firstName: reg.registrationInfo?.primaryCamperName?.split(' ')[0] || registration.firstName || '',
      lastName: reg.registrationInfo?.primaryCamperName?.split(' ').slice(1).join(' ') || registration.lastName || '',
      email: reg.registrationInfo?.email || registration.email || '',
      address: reg.registrationInfo?.address?.street || '',
      city: reg.registrationInfo?.address?.town || '',
      province: reg.registrationInfo?.address?.province || '',
      postalCode: reg.registrationInfo?.address?.postalCode || '',
    },
    accommodationFees: {
      placeName: registration.financial?.accommodationFees?.placeName || '',
      placeCode: registration.financial?.accommodationFees?.priceCode || '',
      fee: registration.financial?.accommodationFees?.accommodationFee || 0,
      numberOfNights: (registration.financial?.accommodationFees as any)?.numberOfNights || 0,
      override: reg.financial?.overrides?.accommodation?.enabled ? {
        amount: reg.financial.overrides.accommodation.amount,
        note: reg.financial.overrides.accommodation.notes
      } : undefined
    },
    registrationFees: [
      ...(reg.financial?.registrationFees?.adults || []).map((adult: any) => ({
        name: adult.name,
        category: 'Adult',
        nights: adult.nights || 0,
        fee: adult.fee
      })),
      ...(reg.financial?.registrationFees?.adultStudents || []).map((student: any) => ({
        name: student.name,
        category: 'Adult Student',
        nights: student.nights || 0,
        fee: student.fee
      })),
      ...(reg.financial?.registrationFees?.youthChildren || []).map((youth: any) => ({
        name: youth.name,
        category: 'Youth/Children',
        nights: youth.nights || 0,
        fee: youth.fee
      }))
    ],
    registrationOverride: reg.financial?.overrides?.registration?.amount !== undefined && reg.financial?.overrides?.registration?.amount !== 0 ? {
      amount: reg.financial.overrides.registration.amount,
      note: reg.financial.overrides.registration.notes
    } : undefined,
    mealFees: {
      fullCampAdult: reg.financial?.otherFees?.meals?.fullCampPassesAdult ? {
        quantity: reg.financial.otherFees.meals.fullCampPassesAdult.quantity || 0,
        unitPrice: reg.financial.otherFees.meals.fullCampPassesAdult.unitPrice || 0,
        total: reg.financial.otherFees.meals.fullCampPassesAdult.total || 0
      } : undefined,
      fullCampChild: reg.financial?.otherFees?.meals?.fullCampPassesChild ? {
        quantity: reg.financial.otherFees.meals.fullCampPassesChild.quantity || 0,
        unitPrice: reg.financial.otherFees.meals.fullCampPassesChild.unitPrice || 0,
        total: reg.financial.otherFees.meals.fullCampPassesChild.total || 0
      } : undefined,
      dailyAdult: reg.financial?.otherFees?.meals?.dailyPassesAdult ? {
        quantity: reg.financial.otherFees.meals.dailyPassesAdult.quantity || 0,
        unitPrice: reg.financial.otherFees.meals.dailyPassesAdult.unitPrice || 0,
        total: reg.financial.otherFees.meals.dailyPassesAdult.total || 0
      } : undefined,
      dailyChild: reg.financial?.otherFees?.meals?.dailyPassesChild ? {
        quantity: reg.financial.otherFees.meals.dailyPassesChild.quantity || 0,
        unitPrice: reg.financial.otherFees.meals.dailyPassesChild.unitPrice || 0,
        total: reg.financial.otherFees.meals.dailyPassesChild.total || 0
      } : undefined,
      override: reg.financial?.overrides?.mealFees?.amount !== undefined && reg.financial?.overrides?.mealFees?.amount !== 0 ? {
        amount: reg.financial.overrides.mealFees.amount,
        note: reg.financial.overrides.mealFees.notes
      } : undefined
    },
    otherFees: {
      fridgeRental: reg.financial?.otherFees?.fridge?.cost || 0,
      donation: reg.financial?.otherFees?.donations?.amount || 0,
      donationNote: reg.financial?.otherFees?.donations?.note || '',
      customFee: reg.financial?.otherFees?.customFee?.amount || 0,
      customFeeNote: reg.financial?.otherFees?.customFee?.note || '',
      otherFees: reg.financial?.otherFees?.otherFees || [],
      override: reg.financial?.overrides?.otherFees?.amount !== undefined && reg.financial?.overrides?.otherFees?.amount !== 0 ? {
        amount: reg.financial.overrides.otherFees.amount,
        note: reg.financial.overrides.otherFees.notes
      } : undefined
    },
    totalAmount: 0,
    paymentAmount: paymentTotal,
    remainingBalance: 0
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', padding: '2rem 1rem' }}>
      <InvoiceDisplay
        registration={invoiceData}
        invoiceNumber={registration.invoiceNumber || 'Not Finalized'}
        invoiceDate={reg.financial?.invoiceDate || null}
      />
    </div>
  );
};

export default CamperInvoicePage;
